import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'

import { FxIcon } from '@/components/fx'

/**
 * The design system's dialog shell: `radius-xl`, `shadow-overlay`, no border,
 * on the scrim the canvas draws behind every dialog.
 *
 * `components/ui/dialog` is the shadcn one — bordered, `rounded-lg`, a
 * `bg-black/80` scrim — and looks like a different product next to an Fx
 * panel. There is no `FxDialog` in `components/fx` (only `FxUploadDialog`,
 * which is that one dialog and not a shell), so this is a local one. It
 * should move into `components/fx` the moment a second wave needs it —
 * reported.
 *
 * Radix supplies the focus trap, the Escape handling, the scroll lock and the
 * `aria-labelledby` wiring, so none of that is reimplemented here.
 */
export function ProjektDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/55" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] w-[min(560px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 flex-col gap-5 overflow-y-auto rounded-xl bg-surface p-8 text-body text-text shadow-overlay">
          <div className="flex items-start gap-4">
            <Dialog.Title className="m-0 flex-1 text-dialog-title">{title}</Dialog.Title>
            <Dialog.Close
              className="-mr-2 -mt-2 flex h-11 w-11 flex-none items-center justify-center rounded-sm text-text"
              aria-label="Dialog schließen"
            >
              <FxIcon name="close" size={24} />
            </Dialog.Close>
          </div>
          {description && (
            <Dialog.Description className="m-0 text-body text-text-muted">
              {description}
            </Dialog.Description>
          )}
          {children}
          {footer && <div className="flex flex-wrap justify-end gap-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
